import Const from 'constants';
import {resolveExposuresOverlap} from 'helpers/exposure';
import {initSportConfig} from 'helpers/common-helper';
import {getTeamNamesOfGames} from 'helpers/game-helper';
import {generateSignature} from 'helpers/hash';

let sportConfig = initSportConfig();

const defaultTeamState = {
    active: false,
    checked: true,
    exposure: {min: 0, max: 100},
};

const defaultStackState = {
    playersQuantity: Const.MLB_NON_CONTROLLED_STACK_MIN_PLAYERS_QUANTITY,
    order: Const.DEFAULT_VALUES_OF_LINEUPS_MLB_FILTER.order,
    exposure: {
        min: 0,
        max: 100,
    },
    checked: true,
    teams: {},
};

const defaultStacksState = {
    [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: cp(defaultStackState),
    [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: cp(defaultStackState),
};

const defaultCombinationsState = {
    '2-2': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 2,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 2,
        },
    },
    '3-2': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 3,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 2,
        },
    },
    '3-3': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 3,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 3,
        },
    },
    '4-X': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 4,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 1,
        },
    },
    '4-2': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 4,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 2,
        },
    },
    '4-3': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 4,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 3,
        },
    },
    '4-4': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 4,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 4,
        },
    },
    '5-X': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 5,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 1,
        },
    },
    '5-2': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 5,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 2,
        },
    },
    '5-3': {
        checked: true,
        exposure: 0,
        playersQuantity: {
            [Const.MLB_NON_CONTROLLED_FIRST_GROUP]: 5,
            [Const.MLB_NON_CONTROLLED_SECOND_GROUP]: 3,
        },
    },
};

function getCombinationsStateBySite(site) {
    let comboKeys = [];
    if (Const.SITE_DK === site) {
        comboKeys = ['2-2', '3-2', '3-3', '4-X', '4-2', '4-3', '4-4', '5-X', '5-2', '5-3'];
    } else if (Const.SITE_FD === site) {
        comboKeys = ['2-2', '3-2', '3-3', '4-X', '4-2', '4-3', '4-4'];
    }

    const combinationsState = {};
    comboKeys.map((comboKey) => {
        combinationsState[comboKey] = {...defaultCombinationsState[comboKey]};
    });

    return {...combinationsState};
}

let combinationsState = getCombinationsStateBySite(sportConfig['site']);

const defaultState = {
    enabled: false,
    signature: null,
    data: {
        stacks: cp(defaultStacksState),
        combinations: cp(combinationsState),
        chooseTeamsRandomly: false,
    },
    isEnabled() {
        return this.enabled;
    },
    getStateId() {
        return 'NonControlledHitterStacking.Settings';
    },
    getStacks() {
        return {...this.data.stacks};
    },
    getCombinations() {
        return {...this.data.combinations};
    },
    getCheckedCombinations() {
        const combos = {};
        Object.keys(this.data.combinations).map((comboKey) => {
            const combo = this.data.combinations[comboKey];
            if (combo.checked) {
                combos[comboKey] = {...combo};
            }
        });
        return combos;
    },
    isCombinationChecked(key) {
        return this.data.combinations[key].checked;
    },
    getCombinationExposure(key) {
        return this.data.combinations[key].exposure;
    },
    getPlayersQuantityByStackNumber(stackNumber) {
        return this.data.stacks[stackNumber].playersQuantity;
    },
    isTeamsChosenRandomly() {
        return Boolean(this.data.chooseTeamsRandomly);
    },
    isAllActiveStackTeamsCheckedByStackNumber(stackNumber) {
        const teams = this.getActiveTeamsByStackNumber(stackNumber);
        for (const team of Object.values(teams)) {
            if (!team.checked) {
                return false;
            }
        }

        return true;
    },
    getStackTeamsExposureByTypeAndStackNumber(exposureType, stackNumber) {
        return this.data.stacks[stackNumber].exposure[exposureType];
    },
    getStackTeamsMinExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber('min', stackNumber);
    },
    getStackTeamsMaxExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber('max', stackNumber);
    },
    getBattingOrderByStackNumber(stackNumber) {
        return this.data.stacks[stackNumber].order;
    },
    isTeamCheckedInStackNumber(team, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams.hasOwnProperty(team) && stack.teams[team].hasOwnProperty('checked')) {
            return stack.teams[team].checked;
        }
        return stack.checked;
    },
    getTeamExposureInStackNumber(team, exposureType, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams.hasOwnProperty(team) && stack.teams[team].hasOwnProperty('exposure')) {
            return stack.teams[team].exposure[exposureType];
        }
        return stack.exposure[exposureType];
    },
    getTotalMinExposureOfCheckedActiveTeamsInStackNumber(stackNumber) {
        const stack = this.data.stacks[stackNumber];

        let totalMinExposure = 0;
        Object.values(stack.teams).map((teamItem) => {
            if (teamItem.checked && teamItem.active) {
                totalMinExposure += teamItem.exposure.min;
            }
        });

        return totalMinExposure;
    },
    getActiveTeamsByStackNumber(stackNumber) {
        const activeTeams = {};

        if (!this.data.stacks.hasOwnProperty(stackNumber)) {
            return activeTeams;
        }

        const {teams} = this.data.stacks[stackNumber];

        Object.keys(teams).map((teamName) => {
            if (teams[teamName].active) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getExposureSumOfCheckedTeamsByStackNumber(stackNumber) {
        const teams = this.getActiveTeamsByStackNumber(stackNumber);
        let exposureSum = 0;
        Object.keys(teams).map((teamName) => {
            if (this.isTeamCheckedInStackNumber(teamName, stackNumber)) {
                exposureSum += this.getTeamMaxExposureInStackNumber(teamName, stackNumber);
            }
        });
        return exposureSum;
    },
    getCheckedTeamNamesByStackNumber(stackNumber) {
        const teams = this.getActiveTeamsByStackNumber(stackNumber);
        const checkedTeamNames = [];
        Object.keys(teams).map((teamName) => {
            if (this.isTeamCheckedInStackNumber(teamName, stackNumber)) {
                checkedTeamNames.push(teamName);
            }
        });
        return checkedTeamNames;
    },
    getTeamMinExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'min', stackNumber);
    },
    getTeamMaxExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'max', stackNumber);
    },
    getSignature() {
        return this.signature;
    },
};

export default function (state = defaultState, action) {
    switch (action.type) {
        case Const.NON_CONTROLLED_HITTER_STACKING_IS_USED:
            state = {...state};
            state.enabled = Boolean(action.use);
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_COMBO_IS_CHECKED:
            state = {...state};
            {
                const combination = state.data.combinations[action.comboKey];
                combination.checked = action.checked;
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_COMBO_EXPOSURE:
            state = {...state};
            {
                const combination = state.data.combinations[action.comboKey];
                combination.exposure = action.exposure;
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_TEAMS_ARE_CHOSEN_RANDOMLY:
            state = {...state};
            state.data.chooseTeamsRandomly = Boolean(action.chooseTeamsRandomly);
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_PLAYERS_QUANTITY:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                stack.playersQuantity = action.playersQuantity;
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_TEAMS:
            state = {...state};

            {
                Object.values(state.data.stacks).map((stack) => {
                    const teams = {};

                    Object.keys(stack.teams).map((teamName) => {
                        teams[teamName] = stack.teams[teamName];
                        teams[teamName].active = inArray(teamName, action.teams);
                    });

                    stack.teams = teams;
                });
            }

            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_STACK_TEAMS_IS_CHECKED:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                stack.checked = action.checked;
                Object.values(stack.teams).map((team) => {
                    if (team.active) {
                        team.checked = action.checked;
                    }
                });
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_STACK_TEAMS_EXPOSURE:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                Object.keys(action.exposure).map((exposureType) => {
                    const value = action.exposure[exposureType];
                    stack.exposure[exposureType] = value;

                    Object.values(stack.teams).map((team) => {
                        const currentExposure = {...team.exposure};
                        currentExposure[exposureType] = value;
                        team.exposure = resolveExposuresOverlap(currentExposure, exposureType);
                    });
                });
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_BATTING_ORDER:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                stack.order = {...stack.order, ...action.order};
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_TEAM_IS_CHECKED:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                if (!stack.teams.hasOwnProperty(action.team)) {
                    stack.teams[action.team] = {...defaultTeamState};
                }
                stack.teams[action.team].checked = Boolean(action.checked);
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_TEAM_EXPOSURE:
            state = {...state};
            {
                const stack = state.data.stacks[action.stackNumber];
                if (!stack.teams.hasOwnProperty(action.team)) {
                    stack.teams[action.team] = {...defaultTeamState};
                }
                stack.teams[action.team].exposure = action.exposure;
            }
            return state;

        case Const.NON_CONTROLLED_HITTER_STACKING_SET_DATA_FROM_STORAGE:
            state = {...state};

            {
                state.data = cp(action.data);
            }

            return state;

        case Const.GET_GAMES_SUCCESS:
            state = {...state};

            {
                const teamNames = getTeamNamesOfGames(action.data);

                Object.values(state.data.stacks).map((stack) => {
                    const teams = {};

                    teamNames.map((teamName) => {
                        if (stack.teams.hasOwnProperty(teamName)) {
                            teams[teamName] = stack.teams[teamName];
                        } else {
                            teams[teamName] = cp(defaultTeamState);
                        }
                    });

                    stack.teams = teams;
                });
            }

            return state;

        case Const.USE_HITTERS_STACK:
            if (action.data) {
                state = {...state};
                state.enabled = false;
            }

            return state;

        case Const.FILTER_BY_SITE:
            combinationsState = getCombinationsStateBySite(action.site);

            return {
                ...defaultState,
                data: {
                    stacks: cp(defaultStacksState),
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.RESET_ALL_SETTINGS:
            const {stacks} = state.data;
            const stacksState = cp(defaultStacksState);

            Object.keys(stacks).map((stackGroup) => {
                Object.keys(stacks[stackGroup].teams).map((teamName) => {
                    stacksState[stackGroup].teams[teamName] = {...defaultTeamState, active: true};
                });
            });

            return {
                ...defaultState,
                data: {
                    stacks: stacksState,
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.FILTER_BY_SLATE:
            return {
                ...defaultState,
                data: {
                    stacks: cp(defaultStacksState),
                    combinations: cp(combinationsState),
                    chooseTeamsRandomly: false,
                },
                enabled: false,
                signature: generateSignature(),
            };

        case Const.NON_CONTROLLED_HITTER_STACKING_MIRROR_STACK: {
            state = {...state};

            state.data.stacks[action.destinationStackNumber] = cp(state.data.stacks[action.sourceStackNumber]);

            return state;
        }
        default:
            return state;
    }
}
